import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Mail, Loader2, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { api } from "../../lib/api";
import { PreviewBanner } from "./PreviewBanner";
import { setupAdminApp, isStandalone } from "../../lib/push";

const home = () => {
  const next = new URLSearchParams(window.location.search).get("next") || "";
  return next.startsWith("/admin/dashboard") ? next : isStandalone() ? "/admin/dashboard?tab=chats" : "/admin/dashboard";
};

const AdminLogin = () => {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setupAdminApp();
    if (localStorage.getItem("sw_admin_token")) {
      api.get("/auth/me").then(() => nav(home())).catch(() => localStorage.removeItem("sw_admin_token"));
    }
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("sw_admin_token", data.token);
      nav(home());
    } catch (e2) {
      setErr((e2?.response?.status || 500) >= 500 ? "The server is restarting after an update. Please wait a minute and tap Sign In again." : e2?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full rounded-xl border border-[#dcebe8] bg-white py-3 pl-11 pr-4 text-[15px] outline-none transition-colors focus:border-[#0e8c7f] focus:ring-2 focus:ring-[#0e8c7f]/15";
  return (
    <>
    <PreviewBanner />
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden overflow-hidden bg-[#0f2e2a] p-14 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-[#0e8c7f]/40 blur-3xl" />
        <div className="inline-flex w-fit rounded-xl bg-white px-4 py-3"><img src="/logo.png" alt="StripWise" className="h-9" /></div>
        <div className="relative">
          <h1 className="font-jakarta text-[44px] font-extrabold leading-tight">Agent Portal</h1>
          <p className="mt-4 max-w-md text-[17px] text-white/70">Manage products and prices, edit your website content, reply to live chats and track quote requests — all in one place.</p>
        </div>
        <p className="text-sm text-white/40">© {new Date().getFullYear()} StripWise</p>
      </div>
      <div className="flex items-center justify-center bg-[#f4fbf9] p-6">
        <form onSubmit={submit} className="w-full max-w-[420px] rounded-[26px] border border-[#e0f0ec] bg-white p-8 shadow-[0_30px_60px_-40px_rgba(15,46,42,.4)] sw-fade-up" data-testid="admin-login-form">
          <Link to="/" className="mb-6 inline-flex items-center gap-1.5 text-[14px] font-medium text-[#0e8c7f] hover:underline"><ArrowLeft size={15} />Back to website</Link>
          <img src="/logo.png" alt="StripWise" className="mb-6 h-9 lg:hidden" />
          <h2 className="text-[28px] font-bold text-[#0f2e2a]">Sign in</h2>
          <p className="mt-1 text-[15px] text-[#5b6d6a]">Admin & agent access only.</p>
          <div className="mt-7 space-y-4">
            <div className="relative">
              <Mail size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8a9a97]" />
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={input} data-testid="admin-email-input" />
            </div>
            <div className="relative">
              <Lock size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8a9a97]" />
              <input type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={input} data-testid="admin-password-input" />
              <button type="button" onClick={() => setShow(!show)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8a9a97]" aria-label="Toggle password">{show ? <EyeOff size={17} /> : <Eye size={17} />}</button>
            </div>
          </div>
          {err && <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-[14px] text-red-600" data-testid="admin-login-error">{err}</p>}
          <button type="submit" disabled={busy} className="sw-btn-primary mt-6 w-full disabled:opacity-60" data-testid="admin-login-submit">
            {busy && <Loader2 size={16} className="animate-spin" />} Sign In
          </button>
        </form>
      </div>
    </div>
    </>
  );
};

export default AdminLogin;
